// ============================================================================
// Parent-scoped recurring window checks.
// ----------------------------------------------------------------------------
// A recurring checklist is visible on a Daily / Weekly canvas date only when
// that date falls inside the item's `recurringWindow` (mirrored from the
// parent element's start/end).
//
// A MISSING window means the item was written without the parent range mirror.
// The correct behavior is to HIDE it — otherwise the item bleeds onto every
// focused day (this was the AI-generated recurring bug: parent starts next
// month, but the item was showing today/tomorrow/this-week regardless). Every
// current write path (checklistWriter.createChecklistNative,
// TimelineContext.syncChecklistBoardItems, recurringWindowMirror) sets the
// window; anything that reaches this filter without one is either a
// pre-refactor legacy doc or a broken write, and both deserve to be hidden
// until re-saved.
//
// The helpers work on plain ISO strings so callers can pass either the raw
// definition field or the projected ChecklistItem field without conversion.
// ============================================================================
import type { RecurringMode } from '../types/recurringChecklist'

export type RecurringWindow = {
  parentElementId?: string
  startDate?: string
  endDate?: string
}

const toDayIso = (value: string | undefined | null): string | null => {
  if (typeof value !== 'string') return null
  const day = value.length >= 10 ? value.slice(0, 10) : value
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

/** Add `n` whole days to a `YYYY-MM-DD` string, returning the same shape.
 *  Uses local-time date construction to match `isoDate` in
 *  recurringChecklistService — cross-TZ drift is intentionally out of scope. */
const addDaysIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d + n)
  const yy = date.getFullYear().toString().padStart(4, '0')
  const mm = (date.getMonth() + 1).toString().padStart(2, '0')
  const dd = date.getDate().toString().padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/**
 * Does the recurring item's window include the focused date?
 *
 * @param focusedDateIso `YYYY-MM-DD` — for DAILY, the calendar day; for
 *                       WEEKLY, the Monday of the focused week.
 * @param mode           DAILY compares day-to-day; WEEKLY treats the focused
 *                       week (Mon–Sun) as a range and checks range overlap.
 * @param window         The item's recurringWindow. `undefined` / missing
 *                       fields → returns FALSE (item is hidden until the
 *                       parent-scoped window is written; see file header).
 */
export const isRecurringItemVisibleOnDate = (
  focusedDateIso: string,
  mode: RecurringMode,
  window: RecurringWindow | null | undefined,
): boolean => {
  if (!window) return false
  const start = toDayIso(window.startDate)
  const end = toDayIso(window.endDate)
  if (!start || !end) return false
  if (mode === 'DAILY') {
    return focusedDateIso >= start && focusedDateIso <= end
  }
  const weekStart = focusedDateIso
  const weekEnd = addDaysIso(focusedDateIso, 6)
  // Weekly overlap: item visible if the [weekStart..weekEnd] range intersects
  // the window at all. Simplest test: not (weekEnd < start) and not
  // (weekStart > end).
  return !(weekEnd < start) && !(weekStart > end)
}

/**
 * Compute the min / max navigable dates for the paginator by scanning every
 * loaded recurring item's window. Missing / partial windows are ignored;
 * when no items have windows the function returns null,null and the caller
 * falls back to defaults.
 */
export const spanOfRecurringWindows = (
  items: readonly { checklistType?: string | null; recurringWindow?: RecurringWindow | null }[],
  mode: RecurringMode,
): { minStart: string | null; maxEnd: string | null } => {
  let minStart: string | null = null
  let maxEnd: string | null = null
  for (const item of items) {
    if (item.checklistType !== mode) continue
    const start = toDayIso(item.recurringWindow?.startDate)
    const end = toDayIso(item.recurringWindow?.endDate)
    if (start && (minStart === null || start < minStart)) minStart = start
    if (end && (maxEnd === null || end > maxEnd)) maxEnd = end
  }
  return { minStart, maxEnd }
}
